import React, { useState, useRef, useEffect } from 'react';
import { CONTACT_INFO } from '../data/content';
import './Contact.css';

/* ── Location Data ── */
const LOCATIONS = [
  {
    country: 'India',
    city: ' Southeast Tamil Nadu',
    flag: '🇮🇳',
    color: '#ff9933',
    g1: '#ff6b00',
    g2: '#ff9933',
    address: 'Coimbatore, Tamil Nadu, India',
    phone: '+91 98945 20497',
    timezone: 'IST — UTC +5:30',
    lon: 78.9,
    lat: 20.6,
  },
  {
    country: 'USA',
    city: 'North America',
    flag: '🇺🇸',
    color: '#3b82f6',
    g1: '#1d4ed8',
    g2: '#3b82f6',
    address: 'United States of America',
    phone: '+1 (800) 000-0000',
    timezone: 'EST — UTC −5:00',
    lon: -98.5,
    lat: 39.5,
  },
  {
    country: 'Japan',
    city: 'Asia Pacific',
    flag: '🇯🇵',
    color: '#ef4444',
    g1: '#b91c1c',
    g2: '#ef4444',
    address: 'Tokyo, Japan',
    phone: '+81 00-0000-0000',
    timezone: 'JST — UTC +9:00',
    lon: 138.3,
    lat: 36.2,
  },
  {
    country: 'Vietnam',
    city: 'Southeast Asia',
    flag: '🇻🇳',
    color: '#22c55e',
    g1: '#15803d',
    g2: '#22c55e',
    address: 'Ho Chi Minh City, Vietnam',
    phone: '+84 00-000-0000',
    timezone: 'ICT — UTC +7:00',
    lon: 106.0,
    lat: 16.0,
  },
  {
    country: 'Malaysia',
    city: 'Southeast Asia',
    flag: '🇲🇾',
    color: '#df08cd',
    g1: '#c82077',
    g2: '#f50303',
    address: 'Kuala Lumpur, Malaysia',
    phone: '+60 00-000-0000',
    timezone: 'MYT — UTC +8:00',
    lon: 116.5,
    lat: 2.0,
  },
];

/* ── Animated Location Card ── */
function LocationCard({ loc, index, active, onClick }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`loc-card${visible ? ' loc-card--visible' : ''}${active ? ' loc-card--active' : ''}`}
      style={{ '--lc': loc.color, '--lg1': loc.g1, '--lg2': loc.g2, transitionDelay: `${index * 0.1}s` }}
      onClick={() => onClick(index)}
    >
      <div className="loc-card-bg" />
      <div className="loc-card-border" />
      <div className="loc-card-glow" />

      <div className="loc-card-top">
        <div className="loc-flag">{loc.flag}</div>
        <div className="loc-card-head">
          <div className="loc-country">{loc.country}</div>
          <div className="loc-desc">{loc.city}</div>
        </div>
        <div className="loc-pulse-dot" />
      </div>

      <div className="loc-card-body">
        <div className="loc-info-row">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {loc.address}
        </div>
        <div className="loc-info-row">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.85a16 16 0 0 0 6 6l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.02z" />
          </svg>
          {loc.phone}
        </div>
        <div className="loc-info-row">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {loc.timezone}
        </div>
      </div>

      <div className="loc-card-scan" />
    </div>
  );
}

/* ── Mercator projection helper ── */
function project(lon, lat, W, H) {
  const x = (lon + 180) / 360 * W;
  const latRad = lat * Math.PI / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = H / 2 - W * mercN / (2 * Math.PI);
  return [x, y];
}


/* ── World Map Canvas ── */
function WorldMap({ activeIndex, onPinClick }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const activeRef = useRef(activeIndex);
  const geoRef = useRef(null);
  const sizeRef = useRef({ W: 0, H: 0 });

  useEffect(() => { activeRef.current = activeIndex; }, [activeIndex]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    function resize() {
      const W = canvas.offsetWidth;
      const isMobile = W < 600;
      const H = Math.round(W * (isMobile ? 0.75 : 0.38));
      canvas.width = W * devicePixelRatio;
      canvas.height = H * devicePixelRatio;
      ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      sizeRef.current = { W, H };
    }

    function proj(lon, lat) {
      const { W, H } = sizeRef.current;
      const scale = W < 600 ? 2.2 : 1.55;
      const offset = W < 600 ? 0.12 : 0.05;
      const [x, yRaw] = project(lon, lat, W, H * scale);
      return [x, yRaw - H * offset];
    }
    async function loadGeo() {
      try {
        const r = await fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json');
        const topo = await r.json();
        geoRef.current = {
          land: window.topojson.feature(topo, topo.objects.land),
          countries: window.topojson.feature(topo, topo.objects.countries),
        };
      } catch (e) {
        geoRef.current = null;
      }
    }

    function geoPath(feature) {
      const geom = feature.geometry || feature;
      function ring(coords) {
        coords.forEach(([lon, lat], i) => {
          const [x, y] = proj(lon, lat);
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.closePath();
      }
      ctx.beginPath();
      if (geom.type === 'Polygon') geom.coordinates.forEach(ring);
      else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(p => p.forEach(ring));
    }

    function drawLand() {
      const geo = geoRef.current;
      if (!geo) return;
      const features = geo.land.features || [geo.land];
      ctx.fillStyle = 'rgba(74,144,217,0.13)';
      ctx.strokeStyle = 'rgba(74,144,217,0.30)';
      ctx.lineWidth = 0.5;
      features.forEach(f => { geoPath(f); ctx.fill(); ctx.stroke(); });
    }

    function drawGrid() {
      const { W, H } = sizeRef.current;
      ctx.strokeStyle = 'rgba(74,144,217,0.055)';
      ctx.lineWidth = 0.5;
      ctx.setLineDash([3, 6]);
      for (let lat = -60; lat <= 60; lat += 30) {
        const [, y] = proj(0, lat);
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }
      for (let lon = -180; lon <= 180; lon += 60) {
        const [x] = proj(lon, 0);
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    function bezierPt(t, x1, y1, cx, cy, x2, y2) {
      const mt = 1 - t;
      return [
        mt * mt * x1 + 2 * mt * t * cx + t * t * x2,
        mt * mt * y1 + 2 * mt * t * cy + t * t * y2,
      ];
    }

    function drawArcs() {
      LOCATIONS.forEach((a, i) => {
        LOCATIONS.slice(i + 1).forEach((b, j) => {
          const [ax, ay] = proj(a.lon, a.lat);
          const [bx, by] = proj(b.lon, b.lat);
          const mx = (ax + bx) / 2;
          const my = Math.min(ay, by) - Math.abs(bx - ax) * 0.22;
          const isAct = activeRef.current === i || activeRef.current === (i + j + 1);
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.quadraticCurveTo(mx, my, bx, by);
          ctx.strokeStyle = isAct ? 'rgba(74,144,217,0.5)' : 'rgba(74,144,217,0.13)';
          ctx.lineWidth = isAct ? 1.2 : 0.55;
          ctx.setLineDash([4, 5]);
          ctx.stroke();
          ctx.setLineDash([]);
        });
      });
    }

    function drawPackets(t) {
      LOCATIONS.forEach((a, i) => {
        LOCATIONS.forEach((b, j) => {
          if (j <= i) return; // skip duplicates
          const [ax, ay] = proj(a.lon, a.lat);
          const [bx, by] = proj(b.lon, b.lat);
          const mx = (ax + bx) / 2;
          const my = Math.min(ay, by) - Math.abs(bx - ax) * 0.22 - 20;
          const sp = 0.15 + ((i * 5 + j) % 7) * 0.03;
          const p = ((t * sp + i * 0.3 + j * 0.5) % 1);

          for (let k = 4; k >= 0; k--) {
            const tp = Math.max(0, p - k * 0.018);
            const mt = 1 - tp;
            const tx = mt * mt * ax + 2 * mt * tp * mx + tp * tp * bx;
            const ty = mt * mt * ay + 2 * mt * tp * my + tp * tp * by;
            ctx.beginPath();
            ctx.arc(tx, ty, 2 - k * 0.25, 0, Math.PI * 2);
            const alpha = Math.round((0.7 - k * 0.12) * 255).toString(16).padStart(2, '0');
            ctx.fillStyle = a.color + alpha;
            ctx.fill();
          }

          const mt = 1 - p;
          const px = mt * mt * ax + 2 * mt * p * mx + p * p * bx;
          const py = mt * mt * ay + 2 * mt * p * my + p * p * by;
          ctx.beginPath();
          ctx.arc(px, py, 3.2, 0, Math.PI * 2);
          ctx.fillStyle = a.color;
          ctx.shadowColor = a.color;
          ctx.shadowBlur = 7;
          ctx.fill();
          ctx.shadowBlur = 0;
        });
      });
    }

function drawPins(t) {
  const isMob = sizeRef.current.W < 600; // ← declare FIRST

  LOCATIONS.forEach((loc, i) => {
    const [cx, cy] = proj(loc.lon, loc.lat);
    const isAct = activeRef.current === i;
    const r1 = 11 + Math.sin(t * 2.6 + i * 1.1) * 2.5;
    const r2 = 19 + Math.sin(t * 2.0 + i * 1.1) * 3.5;

    ctx.beginPath(); ctx.arc(cx, cy, r2, 0, Math.PI * 2);
    ctx.strokeStyle = loc.color + '22'; ctx.lineWidth = 0.8; ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, r1, 0, Math.PI * 2);
    ctx.strokeStyle = loc.color + '44'; ctx.lineWidth = 0.9; ctx.stroke();

    if (isAct) {
      const ang = (t * 1.9) % (Math.PI * 2);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, 28, ang - 0.55, ang, false);
      ctx.closePath();
      ctx.fillStyle = loc.color + '16'; ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(ang) * 28, cy + Math.sin(ang) * 28);
      ctx.strokeStyle = loc.color + '88'; ctx.lineWidth = 1.1; ctx.stroke();
    }

    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, isAct ? 14 : 8);
    g.addColorStop(0, loc.color + '50');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.beginPath(); ctx.arc(cx, cy, isAct ? 14 : 8, 0, Math.PI * 2);
    ctx.fillStyle = g; ctx.fill();

    // ── dot size ──
    const dotR = isMob ? (isAct ? 4 : 3) : (isAct ? 6.5 : 4.5);
    ctx.beginPath(); ctx.arc(cx, cy, dotR, 0, Math.PI * 2);
    ctx.fillStyle = loc.color;
    ctx.shadowColor = loc.color; ctx.shadowBlur = 9; ctx.fill(); ctx.shadowBlur = 0;

    ctx.beginPath(); ctx.arc(cx, cy, isAct ? 2.2 : 1.6, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.92)'; ctx.fill();

    // ── flag emoji ──
    ctx.font = `${isAct ? (isMob ? 12 : 18) : (isMob ? 10 : 14)}px serif`;
    ctx.textAlign = 'center';
    ctx.fillText(loc.flag, cx, cy - (isAct ? 26 : 20));

    // ── country label ──
    ctx.font = `700 ${isAct ? (isMob ? 7 : 10) : (isMob ? 6 : 8)}px monospace`;
    ctx.fillStyle = loc.color;
    ctx.fillText(loc.country.toUpperCase(), cx, cy + (isAct ? 23 : 19));

    if (isAct) {
      ctx.font = '400 8px monospace';
      ctx.fillStyle = 'rgba(160,200,240,0.6)';
      ctx.fillText(loc.city, cx, cy + 33);
    }
  });
}

    function drawStars(t) {
      const { W, H } = sizeRef.current;
      const stars = [
        [30, 15], [90, 45], [180, 20], [320, 12], [430, 38], [510, 8], [590, 28],
        [650, 18], [720, 42], [780, 10], [850, 30], [920, 22], [960, 44],
        [100, 85], [260, 75], [440, 90], [620, 68], [800, 80],
      ];
      stars.forEach(([sx, sy]) => {
        const bri = 0.2 + Math.sin(t * 1.3 + sx) * 0.12;
        ctx.beginPath();
        ctx.arc(sx * (W / 1000), sy * (H / 100), 0.85, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${bri})`;
        ctx.fill();
      });
    }

    function frame(ts) {
      const { W, H } = sizeRef.current;
      const t = ts / 1000;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#040a18';
      ctx.fillRect(0, 0, W, H);

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, W, H);
      ctx.clip();

      drawStars(t);
      drawGrid();
      drawLand();
      drawArcs(t);
      drawPackets(t);
      drawPins(t);

      ctx.restore();
      rafRef.current = requestAnimationFrame(frame);
    }

    resize();
    loadGeo().then(() => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(frame);
    });
    rafRef.current = requestAnimationFrame(frame);

    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        cancelAnimationFrame(rafRef.current);
        resize();
        rafRef.current = requestAnimationFrame(frame);
      });
    });
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  function handleCanvasClick(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const { W, H } = sizeRef.current;
    const mx = (e.clientX - rect.left) * (W / rect.width);
    const my = (e.clientY - rect.top) * (H / rect.height);
    let best = -1, bestD = 9999;
    LOCATIONS.forEach((loc, i) => {
      const [x, yRaw] = project(loc.lon, loc.lat, W, H * 1.55);
      const cx = x;
      const cy = yRaw - H * 0.05;
      const d = Math.hypot(mx - cx, my - cy);
      if (d < 40 && d < bestD) { bestD = d; best = i; }
    });
    if (best >= 0) onPinClick(best);
  }

  return (
    <div className="map-wrap">
      <div className="map-bg-glow" />
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', cursor: 'pointer' }}
        onClick={handleCanvasClick}
      />
    </div>
  );
}

/* ══════════════════════════════════════
   VALIDATION HELPERS (frontend)
══════════════════════════════════════ */
function isValidEmailFormat(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

function containsBlockedLinks(text) {
  const urlPattern = /https?:\/\/(?!(?:www\.)?linkedin\.com)[^\s]+|www\.(?!linkedin\.com)[^\s]+\.[a-z]{2,}/gi;
  return urlPattern.test(text);
}

/* ══════════════════════════════════════
   Main Contact Page
══════════════════════════════════════ */
export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeLocation, setActiveLocation] = useState(0);
  const [loading, setLoading] = useState(false);

  /* ── Email format validated on blur ── */
  const handleEmailBlur = () => {
    if (form.email && !isValidEmailFormat(form.email)) {
      setErrors(er => ({ ...er, email: 'Please enter a valid email address (e.g. name@domain.com)' }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) {
      e.email = 'Email is required';
    } else if (!isValidEmailFormat(form.email)) {
      e.email = 'Please enter a valid email address (e.g. name@domain.com)';
    }
    if (!form.message.trim()) {
      e.message = 'Message is required';
    } else if (containsBlockedLinks(form.message)) {
      e.message = 'Links are not allowed in the message. Only LinkedIn profile URLs are permitted.';
    }
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    // Live link check on message field
    if (name === 'message' && containsBlockedLinks(value)) {
      setErrors(er => ({ ...er, message: 'Links are not allowed in the message. Only LinkedIn profile URLs are permitted.' }));
    } else if (errors[name]) {
      setErrors(er => ({ ...er, [name]: '' }));
    }
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setLoading(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else if (data.error === 'invalid_email_format' || data.error === 'invalid_email_domain') {
        setErrors(er => ({ ...er, email: data.message }));
      } else if (data.error === 'blocked_link') {
        setErrors(er => ({ ...er, message: data.message }));
      } else {
        alert('Failed to send. Please try again.');
      }
    } catch (err) {
      alert('Server error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">

      {/* ── Hero Banner ── */}
      <div className="contact-hero">
        <div className="contact-hero-bg" />
        <div className="contact-hero-overlay" />
        <svg
          className="contact-hero-svg"
          viewBox="0 0 1440 400"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="900" cy="120" r="80" fill="none" stroke="rgba(74,144,217,0.15)" strokeWidth="1.5" />
          <circle cx="900" cy="120" r="140" fill="none" stroke="rgba(74,144,217,0.1)" strokeWidth="1" />
          <circle cx="900" cy="120" r="200" fill="none" stroke="rgba(74,144,217,0.06)" strokeWidth="1" />
          <circle cx="1100" cy="280" r="60" fill="none" stroke="rgba(6,182,212,0.12)" strokeWidth="1.5" />
          {[
            { cx: 900, cy: 40, icon: '✉' },
            { cx: 980, cy: 120, icon: '📞' },
            { cx: 900, cy: 200, icon: '💬' },
            { cx: 820, cy: 120, icon: '@' },
            { cx: 1100, cy: 220, icon: '🔧' },
            { cx: 1100, cy: 340, icon: '👤' },
          ].map((item, i) => (
            <g key={i} style={{ animation: `heroPulse 2s ease-in-out ${i * 0.3}s infinite alternate` }}>
              <circle cx={item.cx} cy={item.cy} r="28" fill="rgba(74,144,217,0.12)" stroke="rgba(74,144,217,0.3)" strokeWidth="1.5" />
              <text x={item.cx} y={item.cy + 5} textAnchor="middle" fontSize="16" fill="rgba(255,255,255,0.7)">{item.icon}</text>
            </g>
          ))}
          <rect x="870" y="95" width="120" height="50" rx="8" fill="rgba(6,182,212,0.15)" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
          <text x="930" y="126" textAnchor="middle" fontSize="15" fontWeight="bold" fill="rgba(6,182,212,0.9)" fontFamily="monospace">SUPPORT</text>
        </svg>
        <div className="contact-hero-content">
          <div className="contact-hero-tag">Get In Touch</div>
          <h1 className="contact-hero-title">Contact</h1>
          <p className="contact-hero-sub">Reach out whenever, from wherever</p>
        </div>
      </div>

      {/* ── Contact Section ── */}
      <section id="contact" className="contact-section">
        <div className="contact-divider" />
        <div className="contact-inner">

          {/* Left: Info */}
          <div className="contact-info reveal-left">
            <div className="section-tag">Contact Us</div>
            <h2 className="section-heading contact-heading">
              Let's Build Something{' '}
              <span className="gradient-text">Great Together</span>
            </h2>
            <p className="contact-desc">
              We offer 24-hour global support. Reach out and our engineers will get
              back to you within one business day.
            </p>
            {/* ── 3D Rotating IC Chip ── */}
            <div style={{ flex: 1, margin: '1rem 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <svg
                width="100%" height="auto"
                viewBox="0 0 500 460"
                xmlns="http://www.w3.org/2000/svg"
                style={{ maxWidth: '420px' }}
              >
                <defs>
                  <style>{`
        .chip-body { animation: chipRotate 6s ease-in-out infinite; transform-origin: 250px 210px; }
        .ring1 { animation: ringPulse 3s ease-in-out infinite; transform-origin: 250px 210px; }
        .ring2 { animation: ringPulse 3s ease-in-out 1s infinite; transform-origin: 250px 210px; }
        .ring3 { animation: ringPulse 3s ease-in-out 2s infinite; transform-origin: 250px 210px; }
        .orbit1 { animation: orbit1 4s linear infinite; transform-origin: 250px 210px; }
        .orbit2 { animation: orbit2 5s linear infinite; transform-origin: 250px 210px; }
        .orbit3 { animation: orbit3 3.5s linear infinite; transform-origin: 250px 210px; }
        .trace1 { animation: tracePulse 2s ease-in-out infinite; }
        .trace2 { animation: tracePulse 2s ease-in-out 0.4s infinite; }
        .trace3 { animation: tracePulse 2s ease-in-out 0.8s infinite; }
        .trace4 { animation: tracePulse 2s ease-in-out 1.2s infinite; }
        .trace5 { animation: tracePulse 2s ease-in-out 1.6s infinite; }
        .trace6 { animation: tracePulse 2s ease-in-out 0.2s infinite; }
        .trace7 { animation: tracePulse 2s ease-in-out 0.6s infinite; }
        .trace8 { animation: tracePulse 2s ease-in-out 1.0s infinite; }
        .led-a { animation: ledA 1.2s ease-in-out infinite; }
        .led-b { animation: ledA 1.2s ease-in-out 0.4s infinite; }
        .led-c { animation: ledA 1.2s ease-in-out 0.8s infinite; }
        .pin-glow { animation: pinGlow 2s ease-in-out infinite; }
        .top-shadow { animation: chipRotate 6s ease-in-out infinite; transform-origin: 250px 210px; }
        .label-anim { animation: labelFade 6s ease-in-out infinite; transform-origin: 250px 210px; }

        @keyframes chipRotate {
          0%,100% { transform: rotateY(0deg) rotateX(8deg); }
          25%      { transform: rotateY(20deg) rotateX(12deg) scaleX(0.88); }
          50%      { transform: rotateY(0deg) rotateX(8deg); }
          75%      { transform: rotateY(-20deg) rotateX(4deg) scaleX(0.88); }
        }
        @keyframes ringPulse {
          0%,100% { transform: scale(1); opacity: 0.15; }
          50%      { transform: scale(1.08); opacity: 0.35; }
        }
        @keyframes orbit1 {
          0%   { transform: rotate(0deg) translateX(130px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(130px) rotate(-360deg); }
        }
        @keyframes orbit2 {
          0%   { transform: rotate(60deg) translateX(155px) rotate(-60deg); }
          100% { transform: rotate(420deg) translateX(155px) rotate(-420deg); }
        }
        @keyframes orbit3 {
          0%   { transform: rotate(120deg) translateX(105px) rotate(-120deg); }
          100% { transform: rotate(480deg) translateX(105px) rotate(-480deg); }
        }
        @keyframes tracePulse {
          0%,100% { opacity: 0.3; stroke-width: 1.5; }
          50%      { opacity: 1; stroke-width: 2.5; }
        }
        @keyframes ledA {
          0%,100% { opacity: 1; }
          50%      { opacity: 0.2; }
        }
        @keyframes pinGlow {
          0%,100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
        @keyframes labelFade {
          0%,100% { opacity: 1; }
          25%      { opacity: 0.6; }
          75%      { opacity: 0.6; }
        }
        @keyframes textRingRotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
                </defs>

                <circle className="ring1" cx="250" cy="210" r="148" fill="none" stroke="#2a6dd9" strokeWidth="1" strokeDasharray="6 10" />
                <circle className="ring2" cx="250" cy="210" r="128" fill="none" stroke="#1a4d8a" strokeWidth="0.8" strokeDasharray="8 8" />

                <defs>
                  <path id="textCircle" d="M 250,210 m -155,0 a 155,155 0 1,1 310,0 a 155,155 0 1,1 -310,0" />
                </defs>
                <g style={{ animation: 'textRingRotate 8s linear infinite', transformOrigin: '250px 210px' }}>
                  <text>
                    <textPath href="#textCircle" startOffset="0%"
                      style={{ fontFamily: 'monospace', fontSize: '11px', fill: '#4a90d9', letterSpacing: '6px', fontWeight: '700' }}>
                      CONTACT US ✦ GET IN TOUCH ✦ DWB TECH SOLUTIONS ✦ REACH OUT ✦ PCB EXPERTS ✦
                    </textPath>
                  </text>
                </g>

                <g className="orbit1"><circle cx="250" cy="210" r="6" fill="#00e5ff" opacity="0.9" /></g>
                <g className="orbit2"><circle cx="250" cy="210" r="5" fill="#22c55e" opacity="0.9" /></g>
                <g className="orbit3"><circle cx="250" cy="210" r="4" fill="#ff9933" opacity="0.9" /></g>

                <line className="trace1" x1="195" y1="132" x2="195" y2="60" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace2" x1="215" y1="132" x2="215" y2="40" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />
                <line className="trace3" x1="235" y1="132" x2="235" y2="55" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace4" x1="255" y1="132" x2="255" y2="40" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />
                <line className="trace5" x1="275" y1="132" x2="275" y2="60" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />
                <line className="trace6" x1="295" y1="132" x2="295" y2="48" stroke="#4a90d9" strokeWidth="2" strokeLinecap="round" />

                <line className="trace1" x1="195" y1="288" x2="195" y2="360" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace2" x1="215" y1="288" x2="215" y2="380" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />
                <line className="trace3" x1="235" y1="288" x2="235" y2="365" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />
                <line className="trace4" x1="255" y1="288" x2="255" y2="380" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace5" x1="275" y1="288" x2="275" y2="360" stroke="#4a90d9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace6" x1="295" y1="288" x2="295" y2="372" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />

                <line className="trace1" x1="152" y1="158" x2="60" y2="158" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace2" x1="152" y1="178" x2="40" y2="178" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />
                <line className="trace3" x1="152" y1="198" x2="55" y2="198" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />
                <line className="trace4" x1="152" y1="218" x2="40" y2="218" stroke="#4a90d9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace5" x1="152" y1="238" x2="60" y2="238" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace6" x1="152" y1="258" x2="48" y2="258" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />

                <line className="trace7" x1="348" y1="158" x2="440" y2="158" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />
                <line className="trace8" x1="348" y1="178" x2="460" y2="178" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace1" x1="348" y1="198" x2="445" y2="198" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />
                <line className="trace2" x1="348" y1="218" x2="460" y2="218" stroke="#4a90d9" strokeWidth="2" strokeLinecap="round" />
                <line className="trace3" x1="348" y1="238" x2="445" y2="238" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" />
                <line className="trace4" x1="348" y1="258" x2="455" y2="258" stroke="#2a6dd9" strokeWidth="2" strokeLinecap="round" />

                {[195, 215, 235, 255, 275, 295].map((x, i) => (
                  <g key={`tp-${i}`}>
                    <rect x={x - 5} y={i % 2 === 0 ? 52 : 32} width="10" height="14" rx="2" fill="#0a1f3d" stroke="#2a6dd9" strokeWidth="1" />
                  </g>
                ))}
                {[195, 215, 235, 255, 275, 295].map((x, i) => (
                  <g key={`bp-${i}`}>
                    <rect x={x - 5} y={i % 2 === 0 ? 354 : 370} width="10" height="14" rx="2" fill="#0a1f3d" stroke="#2a6dd9" strokeWidth="1" />
                  </g>
                ))}
                {[158, 178, 198, 218, 238, 258].map((y, i) => (
                  <g key={`lp-${i}`}>
                    <rect x={i % 2 === 0 ? 44 : 28} y={y - 5} width="14" height="10" rx="2" fill="#0a1f3d" stroke="#2a6dd9" strokeWidth="1" />
                  </g>
                ))}
                {[158, 178, 198, 218, 238, 258].map((y, i) => (
                  <g key={`rp-${i}`}>
                    <rect x={i % 2 === 0 ? 442 : 458} y={y - 5} width="14" height="10" rx="2" fill="#0a1f3d" stroke="#2a6dd9" strokeWidth="1" />
                  </g>
                ))}

                <g className="chip-body">
                  <rect x="160" y="142" width="196" height="156" rx="10" fill="#040e24" stroke="#0f3060" strokeWidth="1" />
                  <polygon points="152,132 160,142 160,298 152,288" fill="#061020" stroke="#0f3060" strokeWidth="0.5" />
                  <polygon points="152,132 348,132 356,122 160,122" fill="#081828" stroke="#0f3060" strokeWidth="0.5" />
                  <rect x="152" y="122" width="196" height="156" rx="10" fill="#0a1f3d" stroke="#2a6dd9" strokeWidth="1.8" />
                  <rect x="162" y="132" width="176" height="136" rx="7" fill="#061428" stroke="#1a4d8a" strokeWidth="1" />

                  <line x1="175" y1="155" x2="225" y2="155" stroke="#1a4d8a" strokeWidth="1" opacity="0.8" />
                  <line x1="225" y1="155" x2="225" y2="175" stroke="#1a4d8a" strokeWidth="1" opacity="0.8" />
                  <line x1="225" y1="175" x2="310" y2="175" stroke="#1a4d8a" strokeWidth="1" opacity="0.8" />
                  <line x1="310" y1="155" x2="310" y2="230" stroke="#1a4d8a" strokeWidth="1" opacity="0.8" />
                  <line x1="175" y1="230" x2="310" y2="230" stroke="#1a4d8a" strokeWidth="1" opacity="0.8" />
                  <line x1="175" y1="155" x2="175" y2="245" stroke="#1a4d8a" strokeWidth="1" opacity="0.8" />
                  <line x1="175" y1="245" x2="270" y2="245" stroke="#1a4d8a" strokeWidth="1" opacity="0.8" />

                  <rect x="196" y="158" width="108" height="84" rx="5" fill="#040e24" stroke="#2a6dd9" strokeWidth="1.2" />
                  <rect x="202" y="164" width="96" height="72" rx="3" fill="#020810" stroke="#1a4d8a" strokeWidth="0.8" />

                  {[0, 1, 2, 3].map(i => (
                    <line key={`dh-${i}`} x1="204" y1={172 + i * 14} x2="296" y2={172 + i * 14} stroke="#0f3060" strokeWidth="0.6" opacity="0.7" />
                  ))}
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <line key={`dv-${i}`} x1={208 + i * 14} y1="166" x2={208 + i * 14} y2="234" stroke="#0f3060" strokeWidth="0.6" opacity="0.7" />
                  ))}

                  <text x="250" y="198" textAnchor="middle" fontFamily="monospace" fontSize="9" fill="#4a90d9" fontWeight="700" letterSpacing="1">DWB</text>
                  <text x="250" y="211" textAnchor="middle" fontFamily="monospace" fontSize="7" fill="#06b6d4" letterSpacing="0.5">TECH</text>
                  <text x="250" y="223" textAnchor="middle" fontFamily="monospace" fontSize="6" fill="#2a6dd9" letterSpacing="0.5">PCB-IC-01</text>

                  <circle cx="166" cy="136" r="5" fill="#040e24" stroke="#1a4d8a" strokeWidth="0.8" />

                  <circle className="led-a" cx="318" cy="136" r="4" fill="#22c55e" />
                  <circle className="led-b" cx="318" cy="148" r="4" fill="#06b6d4" />
                  <circle className="led-c" cx="318" cy="160" r="4" fill="#ff9933" />

                  <rect x="174" y="255" width="16" height="8" rx="2" fill="#0a1f3d" stroke="#2a6dd9" strokeWidth="0.8" />
                  <rect x="200" y="255" width="16" height="8" rx="2" fill="#0a1f3d" stroke="#2a6dd9" strokeWidth="0.8" />
                  <rect x="226" y="255" width="16" height="8" rx="2" fill="#0a1f3d" stroke="#06b6d4" strokeWidth="0.8" />
                  <text x="182" y="261" textAnchor="middle" fontFamily="monospace" fontSize="4" fill="#4a90d9">R1</text>
                  <text x="208" y="261" textAnchor="middle" fontFamily="monospace" fontSize="4" fill="#4a90d9">R2</text>
                  <text x="234" y="261" textAnchor="middle" fontFamily="monospace" fontSize="4" fill="#06b6d4">C1</text>
                </g>

                <text x="250" y="435" textAnchor="middle" fontFamily="monospace" fontSize="10"
                  fill="#2a6dd9" opacity="0.6" letterSpacing="3">PCB DESIGN EXPERTS</text>
              </svg>
            </div>
          </div>

          {/* Right: Form */}
          <div className="contact-form-wrap reveal-right">
            {submitted ? (
              <div className="contact-success">
                <div className="success-icon">✅</div>
                <h3 className="success-title">Message Sent!</h3>
                <p className="success-desc">
                  Thank you for reaching out. Our team will contact you within 1 business day.
                </p>
                <button
                  className="btn-primary"
                  style={{ marginTop: '1.5rem', border: 'none', cursor: 'pointer' }}
                  onClick={() => setSubmitted(false)}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <div className="contact-form">
                <h3 className="form-title">Send Us A Message</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input
                      className="form-input"
                      style={errors.name ? { borderColor: '#ef4444' } : {}}
                      name="name" value={form.name} onChange={handleChange}
                      placeholder="John Doe"
                    />
                    {errors.name && <span className="form-error">{errors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input
                      className="form-input"
                      style={errors.email ? { borderColor: '#ef4444' } : {}}
                      name="email" value={form.email} onChange={handleChange}
                      onBlur={handleEmailBlur}
                      placeholder="john@company.com"
                    />
                    {errors.email && <span className="form-error">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input
                      className="form-input"
                      name="phone" value={form.phone} onChange={handleChange}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Required</label>
                    <select
                      className="form-input form-select"
                      name="service" value={form.service} onChange={handleChange}
                    >
                      <option value="">Select a service…</option>
                      <option>ATE Board Design</option>
                      <option>High-Speed Design</option>
                      <option>Library Development</option>
                      <option>ASIC Flow</option>
                      <option>RF Design</option>
                      <option>Verification Services</option>
                      <option>Signal &amp; Power Integrity</option>
                      <option>Voltage &amp; Current Analysis</option>
                      <option>Automated &amp; Manual DFM</option>
                      <option>IT Software Services</option>
                      <option>EDA Conversion</option>
                      <option>Procurement, Fab &amp; Assembly</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    className="form-textarea"
                    style={errors.message ? { borderColor: '#ef4444' } : {}}
                    name="message" value={form.message} onChange={handleChange}
                    placeholder="Tell us about your project requirements… (Only LinkedIn URLs allowed)"
                    rows={5}
                  />
                  {errors.message && <span className="form-error">{errors.message}</span>}
                </div>

                <button
                  className="form-submit"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={loading ? { opacity: 0.75, cursor: 'not-allowed' } : {}}
                >
                  {loading ? (
                    <>
                      <svg
                        width="16" height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        style={{ animation: 'spin 0.8s linear infinite' }}
                      >
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
                      </svg>
                    </>
                  )}
                </button>

                {/* Spinner keyframe injected inline */}
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          GLOBAL PRESENCE — LOCATIONS SECTION
      ══════════════════════════════════════════ */}
      <section className="locations-section">
        <div className="loc-bg-img" />
        <div className="loc-bg-overlay" />
        <div className="loc-bg-grid" />
        <div className="loc-bg-orb loc-bg-orb--1" />
        <div className="loc-bg-orb loc-bg-orb--2" />
        <div className="loc-bg-orb loc-bg-orb--3" />
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`loc-particle loc-particle--${i + 1}`} />
        ))}

        <div className="loc-inner">
          <div className="loc-header">
            <div className="loc-header-tag">Global Presence</div>
            <h2 className="loc-title">
              Our Locations <span className="loc-title-accent">Worldwide</span>
            </h2>
            <p className="loc-subtitle">
              Engineering excellence delivered across 5 strategic global hubs
            </p>
          </div>

          <WorldMap activeIndex={activeLocation} onPinClick={setActiveLocation} />

          <div className="loc-country-list">
            {LOCATIONS.map((loc, i) => (
              <div
                key={loc.country}
                className={`loc-country-item${activeLocation === i ? ' active' : ''}`}
                style={{ '--lc': loc.color }}
                onClick={() => setActiveLocation(i)}
              >
                <span className="loc-country-flag">{loc.flag}</span>
                <span className="loc-country-name">{loc.country}</span>
                <span className="loc-country-desc">{loc.desc}</span>
              </div>
            ))}
          </div>

          <div className="loc-stats-strip">
            {[
              ['5', 'Global Offices'],
              ['24/7', 'Support Coverage'],
              ['5', 'Time Zones'],
              ['15+', 'Countries Served'],
            ].map(([val, label]) => (
              <div key={label} className="loc-stat">
                <div className="loc-stat-val">{val}</div>
                <div className="loc-stat-label">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}